import { forEach, has } from 'lodash';
import {
  SourceConfigById,
  AvailableFeed,
  VendorFeedSpecs,
} from 'new/components/flows/types/elt/CreateELTFlow/types';
import { processV2Template } from './utils/v2TemplateProcessor';
import { processV1Template } from './utils/v1TemplateProcessor';
import { createVendorFeedSpecsWrapper } from './utils/vendorFeedSpecsUtils';

/**
 * ELT Vendor Feed Specs Manager
 *
 * This is the main entry point for processing ELT templates (V1 and V2) and
 * creating the unified VendorFeedSpecs structure that UI components consume.
 *
 * Key Functions:
 * - Determines template version (V1 vs V2) and routes to appropriate processor
 * - Maintains backward compatibility with existing V1 templates
 * - Provides unified capabilities and additional_params structure
 * - Handles parameter value extraction for UI components
 *
 * Architecture:
 * - V1 Templates: Uses processV1Template (maintains original structure + adds capabilities)
 * - V2 Templates: Uses processV2Template (creates new strategy-based structure + capabilities)
 * - Both output unified VendorFeedSpecs with capabilities and additional_params
 */
const DEBUG_LEVEL = 0; //0 : no debug; 1: feed info, 2: all info

/**
 * Helper function to get current parameter values from flat sourceConfig
 * This avoids duplicating the parameter lookup logic across different components
 */
export const getCurrentParameterValues = (
  sourceConfig: any,
  vendorFeedSpecs: VendorFeedSpecs,
  templateVersion?: 'v1' | 'v2',
): {
  objectDetectionModeEnabled: boolean;
  singleSchemaValue: any;
} => {
  let objectDetectionModeEnabled = false;
  let singleSchemaValue = null;

  // Get parameter values directly from sourceConfig (flat structure)
  const paramValues = sourceConfig || {};

  // Create wrapper for unified access to vendor feed specs
  const vendorFeedSpecsWrapper = createVendorFeedSpecsWrapper(
    vendorFeedSpecs,
    templateVersion || 'v1',
  );

  // Get object detection mode value using wrapper
  const objectDetectionParamName = vendorFeedSpecsWrapper.getObjectDetectionModeParam();
  if (objectDetectionParamName) {
    const paramValue = paramValues[objectDetectionParamName];

    if (paramValue !== undefined) {
      objectDetectionModeEnabled =
        paramValue === true ||
        paramValue === 'true' ||
        paramValue === 'enabled' ||
        paramValue === 'Enabled';
    } else {
      console.log(
        'Object detection mode parameter not found in sourceConfig:',
        objectDetectionParamName,
      );
    }
  }

  // Get single schema value using wrapper
  const singleSchemaParamName = vendorFeedSpecsWrapper.getSingleSchemaParam();
  if (singleSchemaParamName) {
    const paramValue = paramValues[singleSchemaParamName];

    if (paramValue !== undefined) {
      singleSchemaValue = paramValue;

      if (DEBUG_LEVEL >= 1) {
        console.log('Single schema parameter found:', {
          paramName: singleSchemaParamName,
          value: singleSchemaValue,
          templateVersion: templateVersion || 'v1',
        });
      }
    } else {
      console.log('Single schema parameter not found in sourceConfig:', singleSchemaParamName);
    }
  }

  return {
    objectDetectionModeEnabled,
    singleSchemaValue,
  };
};

export const getVendorFeedSpecs = (
  sourceConfigById: SourceConfigById,
  selectedConnection: string,
  availableFeeds: AvailableFeed[],
  templateVersionInfo: { templateVersion: 'v1' | 'v2'; isV2Template: boolean } | undefined,
  vendor?: any, // The vendor object
  processedEndpoints?: any, // The processed endpoints
): VendorFeedSpecs => {
  // Safety check: ensure we have the required data
  if (!sourceConfigById || !selectedConnection || !sourceConfigById[selectedConnection]) {
    if (DEBUG_LEVEL >= 1) {
      console.log('getVendorFeedSpecs: Missing required data:', {
        hasSourceConfigById: !!sourceConfigById,
        selectedConnection,
        hasConnectionConfig: !!sourceConfigById?.[selectedConnection],
      });
    }

    // Return empty V1 vendor specs (default)
    return {
      ref_feed_options: {
        display: {},
        feed_names: {},
        ref_field_list_selector: '',
        ref_fields_names: '',
        default_ref_feed: null,
        default_include_feeds: [],
        supports_force_resync: {},
        object_detection_mode_selector_param: undefined,
        single_schema_param: undefined,
      },
      data_feed_options: {},
      strategyMappings: {},
      capabilities: {},
      additional_params: [],
    };
  }

  // Get template version from props (passed from parent components)
  const { templateVersion = 'v1', isV2Template = false } = templateVersionInfo || {};

  if (DEBUG_LEVEL >= 1) {
    console.log('getVendorFeedSpecs: Template version info from props:', {
      templateVersion,
      isV2Template,
    });
  }

  if (isV2Template && vendor && processedEndpoints) {
    // V2 Template Processing - ONLY for V2 templates with vendor and processed endpoints
    if (DEBUG_LEVEL >= 2) {
      console.log('Processing V2 templates with enhanced discovery data');
    }

    const { vendorFeedSpecs, strategyMappings } = processV2Template(
      vendor,
      availableFeeds,
      {}, // Empty discovery results for now
      processedEndpoints, // Pass the processed endpoints
      sourceConfigById[selectedConnection],
    );

    // Store strategy mappings for UI consumption
    (vendorFeedSpecs as any).strategyMappings = strategyMappings;

    return vendorFeedSpecs;
  } else {
    // Extract vendor endpoints for V1 capability processing
    const vendorEndpoints = vendor?.vendor_endpoints || [];

    const { vendorFeedSpecs, strategyMappings, feedBasedOptions } = processV1Template(
      vendor,
      availableFeeds,
      {},
      processedEndpoints,
      sourceConfigById[selectedConnection],
    );

    // Store strategy mappings for UI consumption
    (vendorFeedSpecs as any).strategyMappings = strategyMappings;

    // Process available feeds for V1 templates (exact same logic as before)
    if (availableFeeds && availableFeeds.length > 0) {
      availableFeeds.forEach((feed) => {
        const key = feed.value;
        const allowedFeeds = [];
        const feedTypes = feedBasedOptions.feed_types[key] || {};

        if ('forceFeed' in feedTypes && feedTypes.forceFeed) {
          allowedFeeds.push(feedTypes.forceFeed);
        } else {
          const allowedFeedsSet = new Set<string>(
            vendorFeedSpecs.ref_feed_options.default_include_feeds || [],
          );

          Object.keys(vendorFeedSpecs.ref_feed_options.display).forEach((refFeed) => {
            if (feedTypes[refFeed] === true) {
              allowedFeedsSet.add(refFeed);
            } else if (feedTypes[refFeed] === false) {
              allowedFeedsSet.delete(refFeed);
            }
          });

          allowedFeeds.push(...Array.from(allowedFeedsSet));
        }

        const defaultRefForThisFeed = allowedFeeds.includes(
          vendorFeedSpecs.ref_feed_options.default_ref_feed || '',
        )
          ? vendorFeedSpecs.ref_feed_options.default_ref_feed
          : allowedFeeds[0];

        vendorFeedSpecs.data_feed_options[key] = {
          feed_name: key,
          label: feed.label,
          default_ref: defaultRefForThisFeed,
          ref_feed_selectors: allowedFeeds,
          primary_key_selectors: (() => {
            const primaryKeyConfig = feedBasedOptions.primary_keys[key] ||
              feedBasedOptions.primary_keys['nx_all'] || { primary_key: '' };

            if (primaryKeyConfig.primary_key.includes('{feed_name}')) {
              return {
                primary_key: primaryKeyConfig.primary_key.replace('{feed_name}', key),
              };
            }
            return primaryKeyConfig;
          })(),
        };
      });
    }

    return vendorFeedSpecs;
  }
};
